import { useState } from 'react'
import {
  Button, Checkbox, CloseButton, Drawer, Popover, Portal, RadioGroup, Stack, Text, VStack,
} from '@chakra-ui/react'
import { FiChevronDown } from 'react-icons/fi'
import { useIsMobile } from '../../../hooks/useIsMobile'
import type { CandidateKind, CandidateSearchRequest, CorridorWidth } from '../../../types/turf'

const KINDS: { value: CandidateKind; label: string }[] = [
  { value: 'Worksites', label: 'Worksites' },
  { value: 'Homes', label: 'Homes' },
  { value: 'Both', label: 'Both' },
]
const CORRIDORS: { value: CorridorWidth; label: string }[] = [
  { value: 'RightOnTheWay', label: 'Right on the way' },
  { value: 'ShortDetour', label: 'Short detour' },
]

function Choice<T extends string>({ label, value, options, onChange }: {
  label: string
  value: T
  options: { value: T; label: string }[]
  onChange: (v: T) => void
}) {
  return (
    <VStack align="stretch" gap={1}>
      <Text fontSize="xs" fontWeight="medium" color="fg.muted" textTransform="uppercase">{label}</Text>
      <RadioGroup.Root
        size="sm"
        colorPalette="brand"
        aria-label={label}
        value={value}
        onValueChange={(e) => { if (e.value) onChange(e.value as T) }}
      >
        <Stack direction="row" gap={4} wrap="wrap">
          {options.map(o => (
            <RadioGroup.Item key={o.value} value={o.value} minH={{ base: '44px', md: '32px' }}>
              <RadioGroup.ItemHiddenInput />
              <RadioGroup.ItemIndicator />
              <RadioGroup.ItemText>{o.label}</RadioGroup.ItemText>
            </RadioGroup.Item>
          ))}
        </Stack>
      </RadioGroup.Root>
    </VStack>
  )
}

/** [O4] "Find nearby stops": what to look for, how far off the route, same employer only.
 *  The distances are server constants — only the enums are sent. Popover on desktop, bottom sheet on mobile. */
export function FindNearbyMenu({ onSearch, loading }: {
  onSearch: (body: CandidateSearchRequest) => void
  loading: boolean
}) {
  const isMobile = useIsMobile()
  const [open, setOpen] = useState(false)
  const [kind, setKind] = useState<CandidateKind>('Worksites')
  const [corridor, setCorridor] = useState<CorridorWidth>('RightOnTheWay')
  const [sameEmployer, setSameEmployer] = useState(false)
  const homesOnly = kind === 'Homes'

  const submit = () => {
    onSearch({ kind, corridor, sameEmployerOnly: !homesOnly && sameEmployer })
    setOpen(false)
  }

  const form = (
    <VStack align="stretch" gap={4}>
      <Choice label="Look for" value={kind} options={KINDS} onChange={setKind} />
      <Choice label="How far off the route" value={corridor} options={CORRIDORS} onChange={setCorridor} />
      <Checkbox.Root
        size="sm"
        colorPalette="brand"
        checked={sameEmployer && !homesOnly}
        disabled={homesOnly}
        onCheckedChange={(e) => setSameEmployer(!!e.checked)}
        minH={{ base: '44px', md: 'auto' }}
      >
        <Checkbox.HiddenInput />
        <Checkbox.Control><Checkbox.Indicator /></Checkbox.Control>
        <Checkbox.Label>Only same employer(s) as my stops</Checkbox.Label>
      </Checkbox.Root>
      <Button colorPalette="brand" size={{ base: 'lg', md: 'sm' }} onClick={submit}>Show suggestions</Button>
    </VStack>
  )

  const trigger = (
    <Button size="sm" variant="outline" minH={{ base: '44px', md: 'auto' }} loading={loading}
      onClick={isMobile ? () => setOpen(true) : undefined}>
      Find nearby stops <FiChevronDown />
    </Button>
  )

  if (isMobile) {
    return (
      <>
        {trigger}
        <Drawer.Root open={open} placement="bottom" onOpenChange={(e) => setOpen(e.open)}>
          <Portal>
            <Drawer.Backdrop />
            <Drawer.Positioner>
              <Drawer.Content borderTopRadius="xl" maxH="85vh" pb="env(safe-area-inset-bottom, 0px)" shadow="popover">
                <Drawer.CloseTrigger asChild><CloseButton size="sm" minW="44px" minH="44px" /></Drawer.CloseTrigger>
                <Drawer.Header><Drawer.Title>Find nearby stops</Drawer.Title></Drawer.Header>
                <Drawer.Body overflowY="auto" pb={4}>{form}</Drawer.Body>
              </Drawer.Content>
            </Drawer.Positioner>
          </Portal>
        </Drawer.Root>
      </>
    )
  }

  return (
    <Popover.Root open={open} onOpenChange={(e) => setOpen(e.open)} positioning={{ placement: 'bottom-end' }} lazyMount unmountOnExit>
      <Popover.Trigger asChild>{trigger}</Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content w="320px" shadow="popover">
            <Popover.Body p={4}>{form}</Popover.Body>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  )
}
